import React, { useState, useEffect, useMemo, useRef } from "react";
import FormContainer from "components/FormContainer";
import Input from "components/Input";
import PhoneNumberInput from "components/PhoneNumberInput";
import Dropdown from "components/Dropdown";
import Card from "components/Card";
import CardHeader from "components/CardHeader";
import DatePicker from "components/DatePicker";
import TimePicker from "components/TimePicker";
import RoomQuantityTooltip from "../RoomQuantityTooltip";
import {
  GET_AVAILABLE_ROOM_TYPES_BY_DATE_RANGE,
  GET_ALL_RATE_PLANS,
} from "graphql/service";
import { useLazyQuery, useQuery } from "@apollo/client";
import colors from "constants/colors";
import { Row, TravelInputRow, SettingLink } from "./styles";
import { debouncedErrorMessage } from "utils/messageUtil";
import moment from "moment-timezone";
import { TIME_ZONE } from "constants/constant";

const BookingSetUp = ({
  btnInfo,
  checkInDate,
  setCheckInDate,
  checkOutDate,
  setCheckOutDate,
  roomQuantity,
  setRoomQuantity,
  roomType,
  setRoomType,
  ratePlan,
  setRatePlan,
  customerInfo,
  setCustomerInfo,
  customerInfoError,
  setCustomerInfoError,
  isWheelchairAccessible,
  setIsWheelchairAccessible,
}) => {
  
  const [availableRoomTypes, setAvailableRoomTypes] = useState({});
  const [roomQuantityArray, setRoomQuantityArray] = useState([]);

  const phoneNumber = customerInfo.phoneNumber || "";
  const [phoneNumberValidity, setPhoneNumberValidity] = useState(false);

  const updatingRef = useRef(false);

  const handlePhoneChange = (number) => {
    if (updatingRef.current) return;
    if (number === phoneNumber) return;
    // ignore library reset during init
    if (number === "" && phoneNumber !== "") return;
    updatingRef.current = true;
    setCustomerInfo((prev) => ({ 
      ...prev,
      phoneNumber: number 
    }));
    updatingRef.current = false;
  };

  const handleValidityChange = (valid) => {
    setPhoneNumberValidity(valid);
  };

  const [
    getAvailableRoomTypesByDateRange,
    { data: availableRoomTypesData, error: availableRoomTypesError },
  ] = useLazyQuery(GET_AVAILABLE_ROOM_TYPES_BY_DATE_RANGE);

  const { data: ratePlansData, error: ratePlansError } = useQuery(GET_ALL_RATE_PLANS);

  const ratePlans = useMemo(() => {
    if (!ratePlansData?.ratePlans) return {};

    return ratePlansData.ratePlans.reduce((acc, ratePlan) => {
      acc[ratePlan.title] = ratePlan;
      return acc;
    }, {});
  }, [ratePlansData]);

  useEffect(() => {
    if (!availableRoomTypesError || !ratePlansError) return;

    if (!availableRoomTypesError.networkError && !ratePlansError.networkError) {
      let content = availableRoomTypesError.message || ratePlansError.message;

      if (availableRoomTypesError.message.includes('Validation') || ratePlansError.message.includes('Validation')) {
        content = JSON.stringify(
          availableRoomTypesError.graphQLErrors[0]?.extensions?.exception ||
          ratePlansError.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [availableRoomTypesError, ratePlansError]);

  useEffect(() => {
    const types = {};
    if (availableRoomTypesData?.availableRoomTypesByDateRange) {
      const includedRoomTypes = availableRoomTypesData.availableRoomTypesByDateRange.filter(
        (roomType) =>
          ratePlan.roomTypes?.some(
            (r) => r.roomType?.roomTypeId === roomType.roomTypeId
          )
      );
      includedRoomTypes.forEach((type) => {
        if (type.name !== "Amenities") {
          types[type.name] = type;
        }
      });
      setAvailableRoomTypes(types);
    }
  }, [availableRoomTypesData?.availableRoomTypesByDateRange, ratePlan.roomTypes]);

  useEffect(() => {
    if (checkInDate && checkOutDate) {
      getAvailableRoomTypesByDateRange({
        variables: {
          input: {
            checkInDate,
            checkOutDate,
            roomQuantity: 1,
            guestQuantity: 1,
            isWheelchairAccessible: false,
          },
        },
      });
      setRoomType({});
      setRoomQuantityArray([]);
      setRoomQuantity("");
    } else {
      setAvailableRoomTypes({});
      setRoomType({});
      setRoomQuantityArray([]);
      setRoomQuantity("");
    }
  }, [checkInDate, checkOutDate]);

  const createNumberArray = (arraylength) => {
    if (arraylength < 1) {
      return [];
    }

    return Array.from({ length: arraylength }, (_, index) => index + 1);
  };

  const handleChangeRoomType = (e) => {
    setRoomType(availableRoomTypes[e]);
    setRoomQuantityArray(
      createNumberArray(availableRoomTypes[e].availableRooms)
    );
  };

  const handleChangeRatePlan = (e) => {
    setRatePlan(ratePlans[e]);
  };

  return (
    <Card>
      <CardHeader>
        <div className="type">GROUP BOOKING SET UP</div>
        {btnInfo && <div className="buttonContainer">{btnInfo}</div>}
      </CardHeader>
      <FormContainer>
        <Row>
          <Input
            mainPage
            title="GROUP NAME"
            value={customerInfo.organization || ""}
            error={customerInfoError.organizationError}
            onChange={(e) => {
              setCustomerInfo({ ...customerInfo, organization: e.target.value });
            }}
            additionalstyle={`
              flex-basis: 100%;
            `}
          />
        </Row>
        <Row>
          <Input
            mainPage
            title="EMAIL"
            value={customerInfo.email || ""}
            error={customerInfoError.emailError}
            onChange={(e) => {
              setCustomerInfo({ ...customerInfo, email: e.target.value });
            }}
            additionalstyle={`
              flex-basis: 50%;
              margin-right: 16px;

              @media only screen and (max-width: 767px) {
                flex-basis: 100%;
                margin: 0;
              }
            `}
          />
          <PhoneNumberInput
            mainPage
            title="PHONE"
            error={customerInfoError.phoneNumberError}
            value={phoneNumber}
            onChangeNumber={handlePhoneChange}
            onPhoneValidityChange={handleValidityChange}
            onBlur={() => {          
              setCustomerInfoError((prev) => ({ 
                ...prev,
                phoneNumberError: !phoneNumberValidity && phoneNumber.length > 0
              }));
            }}
            additionalstyle={`
              flex-basis: 50%;
              @media only screen and (max-width: 767px) {
                flex-basis: 100%;
              }
            `}
          />
        </Row>
        <Row>
          <Dropdown
            mainPage
            options={Object.keys(ratePlans)}
            value={ratePlan.title || ""}
            placeholder="-Select-"
            title="RATE PLAN CODE"
            onChange={handleChangeRatePlan}
            additionalstyle={`
              width: calc(50% - 8px);              
            `}
          />
        </Row>
        <SettingLink
          to="/settings/rate-plan"
          target="_blank"
          rel="noopener noreferrer"
        >
          Manage Rate Plans
        </SettingLink>
        <TravelInputRow>
          <div>
            <div className="datePickerContainer">
              <span>ARRIVAL</span>
              <DatePicker
                error={checkInDate ? checkInDate >= checkOutDate : false}
                minDate={moment(new Date()).tz(TIME_ZONE)}
                dateValue={moment(checkInDate).tz(TIME_ZONE)}
                dateOnChange={setCheckInDate}
                additionalstyle={`
                  width: 100%;
                  min-width: 130px;
                  font-size: 18px;
                  font-weight: 600;
                  border: 1px solid ${colors.darkGrey};

                  input {
                    padding-right: 26px;
                  }
                `}
              />
              <span className="datePlaceHolder">MM/DD/YYYY</span>
            </div>
            <div className="timePickerContainer">
              <span>ARRIVAL TIME</span>
              <TimePicker
                TimeValue={moment(checkInDate).tz(TIME_ZONE)}
                setTime={setCheckInDate}
                additionalstyle={`
                  width: 100%;
                  min-width: 110px;
                  border: 1px solid ${colors.darkGrey};

                  input {
                    padding-right: 26px;
                  }
                `}
              />
              <span className="timePlaceHolder">HH:MM</span>
            </div>
          </div>
          <div>
            <div className="datePickerContainer">
              <span>DEPARTURE</span>
              <DatePicker
                error={checkInDate ? checkInDate >= checkOutDate : false}
                minDate={moment(new Date()).tz(TIME_ZONE)}
                dateValue={moment(checkOutDate).tz(TIME_ZONE)}
                dateOnChange={setCheckOutDate}
                additionalstyle={`
                  width: 100%;
                  min-width: 130px;
                  font-size: 18px;
                  font-weight: 600;
                  border: 1px solid ${colors.darkGrey};

                  input {
                    padding-right: 26px;
                  }
                `}
              />
              <span className="datePlaceHolder">MM/DD/YYYY</span>
            </div>
            <div className="timePickerContainer">
              <span>DEPARTURE TIME</span>
              <TimePicker
                TimeValue={moment(checkOutDate).tz(TIME_ZONE)}
                setTime={setCheckOutDate}
                additionalstyle={`
                  width: 100%;
                  min-width: 110px;
                  border: 1px solid ${colors.darkGrey};
                  
                  input {
                    padding-right: 26px;
                  }
                `}
              />
              <span className="timePlaceHolder">HH:MM</span>
            </div>
          </div>
        </TravelInputRow>
        <Row
          additionalstyle={`
            width: calc(50% - 8px);
          `}
        >
          <Dropdown
            mainPage
            options={Object.keys(availableRoomTypes)}
            value={roomType.name || ""}
            placeholder="-Select-"
            title="ROOM TYPE"
            onChange={handleChangeRoomType}
            additionalstyle={`
              width: 50%;
              margin-right: 16px;
            `}
          />
          <RoomQuantityTooltip>
            <div style={{ width: "50%" }}>
              <Dropdown
                mainPage
                disabled={roomQuantityArray.length === 0}
                options={roomQuantityArray}
                value={roomQuantity}
                title="# OF ROOMS"
                onChange={(e) => setRoomQuantity(e)}
                additionalstyle={`
                width: 100%;
              `}
              />
            </div>
          </RoomQuantityTooltip>
        </Row>
      </FormContainer>
    </Card>
  );
};

export default BookingSetUp;
